/**
 * Offline-Fähigkeit für Mitschrift/Lernzettel (Markdown) und das
 * Arbeits-PDF eines Themas.
 *
 * Drei Zustände pro Element (Notiz oder PDF):
 *  - `base:*`    – zuletzt online gesehener Stand (Referenz für den Abgleich).
 *  - `pending:*` – offline entstandene Änderung, EIN Slot pro Element (eine
 *                  erneute Änderung ersetzt die vorherige, es sammelt sich
 *                  nichts an).
 *  - nichts       – unverändert, `base` ist der aktuelle Stand.
 *
 * Sync (Klick auf das Kreis-Pfeile-Symbol, nur online sinnvoll):
 *  - Notiz: Server-Text frisch laden. Ist er (noch) identisch mit `base`,
 *    einfach den Offline-Text hochladen. Weicht er ab (jemand/etwas hat auf
 *    dem Server zwischenzeitlich geändert), NICHTS überschreiben – die
 *    Offline-Zeilen, die im neuen Server-Text fehlen, werden unter einer
 *    eigenen Überschrift angehängt statt etwas zu verlieren.
 *  - PDF: dieselbe Idee, aber grobkörniger. Die Materialien-Prüfsumme
 *    (`mergeKey`, siehe topicPdf.ts) von damals gegen heute vergleichen –
 *    unverändert: die offline bearbeitete Version normal über den
 *    bestehenden Speicherweg (`saveTopicPdf`) zurückschreiben, genau wie ein
 *    normaler Online-Save. Geändert (z. B. eine Datei kam dazu/fiel weg):
 *    NICHT automatisch zusammenführen – Seitenzuordnung könnte nicht mehr
 *    stimmen, ein automatischer Merge könnte falsche Seiten überschreiben.
 *    Bleibt als Konflikt liegen, der Nutzer bearbeitet es manuell neu.
 */

import { pb } from "@/lib/pocketbase";
import { offlineStore } from "./offline-store";
import { topicsService } from "../topics.service";
import { notesService } from "../notes.service";
import { materialsService } from "../materials.service";
import { subjectsService } from "../subjects.service";
import { homeworkService } from "../homework.service";
import { timetableService } from "../timetable.service";
import { mergeKey } from "../document/mergeCache";
import { localFiles, materialOf } from "../document/localFiles";
import { pullAll, pushPending } from "../document/fileSync";
import { invalidateCollection, type QueuedWrite } from "../pocketbase-repository";
import type { Homework, Material, NoteDocument, NoteKind, Subject, TimetableSettings, TimetableSlot, Topic } from "@/types/models";
// Nur der Typ – kostet zur Laufzeit nichts, wird beim Bauen komplett entfernt.
import type { TopicPdf } from "../document/topicPdf";

type OfflinePdfEntry = { blob: Blob; meta: Omit<TopicPdf, "blob" | "sources" | "ignored" | "stamps">; mergeKey: string };

// `document/topicPdf` bleibt bewusst außerhalb des Barrels (zieht pdf-lib
// ~430 kB ins Startbundle, siehe CLAUDE.md) – hier genauso wichtig, weil
// dieser Dienst schon beim App-Start geladen wird (main.ts/AppLayout.ts).
// Dynamischer Import statt eines Top-Level-Imports hält pdf-lib draußen, bis
// tatsächlich ein PDF im Spiel ist.
let topicPdfPromise: Promise<typeof import("../document/topicPdf")> | undefined;
function loadTopicPdf(): Promise<typeof import("../document/topicPdf")> {
  topicPdfPromise ??= import("../document/topicPdf");
  return topicPdfPromise;
}

interface PendingNote {
  noteId: string;
  content: string;
  savedAt: string;
}
interface PendingPdf {
  blob: Blob;
  rotations: [number, number][];
  mergeKeyAtEdit: string;
  savedAt: string;
}

const noteKey = (topicId: string, kind: NoteKind) => `note:${topicId}:${kind}`;
const pdfKey = (topicId: string) => `pdf:${topicId}`;
const LIST_KEYS = {
  subjects: "base:list:subjects",
  topics: "base:list:topics",
  homework: "base:list:homework",
  timetableSlots: "base:list:timetable-slots",
  timetableSettings: "base:list:timetable-settings",
} as const;
const materialsKey = (topicId: string) => `base:materials:${topicId}`;
const ISSUES_KEY = "sync:issues";

/**
 * Ein Problem, das beim Sync entstanden ist und manuelle Klärung braucht
 * (echter Konflikt) oder zumindest zur Kenntnis genommen werden soll
 * (automatisch aufgelöst, aber sichtbar machen). Bleibt bestehen, bis der
 * Nutzer es im Popup (Klick auf den Kreis-Pfeile-Knopf) aktiv erledigt –
 * der Knopf selbst bleibt so lange sichtbar (siehe hasPending()).
 */
export interface SyncIssue {
  id: string;
  kind: "pdf-conflict" | "note-merged" | "op-error";
  topicId?: string;
  noteKind?: NoteKind;
  message: string;
  queuedWrite?: QueuedWrite;
  queueKey?: string;
  at: string;
}

async function loadIssuesRaw(): Promise<SyncIssue[]> {
  return (await offlineStore.get<SyncIssue[]>(ISSUES_KEY)) ?? [];
}
async function saveIssue(issue: SyncIssue): Promise<void> {
  const list = await loadIssuesRaw();
  await offlineStore.set(ISSUES_KEY, [...list.filter((i) => i.id !== issue.id), issue]);
}
async function clearIssueById(id: string): Promise<void> {
  const list = await loadIssuesRaw();
  await offlineStore.set(ISSUES_KEY, list.filter((i) => i.id !== id));
}

/** Aktiv = weder archiviert noch durch Prüfungstermin/Frist erledigt. */
function isRelevant(topic: Topic): boolean {
  return topic.state === "active";
}

export const offlineSync = {
  /**
   * Listen, Notiztexte und Materiallisten aktiver Themen als `base`-Stand
   * ablegen – in wenigen Sammelanfragen. PDFs landen erst beim ersten Öffnen
   * im Gerätespeicher (topicPdf.ts), nicht hier.
   */
  async refreshCache(onProgress?: (done: number, total: number) => void): Promise<void> {
    if (!navigator.onLine) return;
    const allTopics = await topicsService.listAll().catch(() => [] as Topic[]);
    const topics = allTopics.filter(isRelevant);

    // Übersichtsseiten (Startseite, Prüfungen, Hausaufgaben) brauchen nur
    // diese Listen, nicht Mitschrift/PDF – separat cachen, damit sie auch
    // dann noch funktionieren, wenn das Caching einzelner Themen unten
    // fehlschlägt oder abgebrochen wird.
    await Promise.all([
      subjectsService.list().then((s) => offlineStore.set(LIST_KEYS.subjects, s)).catch(() => {}),
      offlineStore.set(LIST_KEYS.topics, allTopics),
      homeworkService.listAll().then((h) => offlineStore.set(LIST_KEYS.homework, h)).catch(() => {}),
      timetableService.listSlots().then((s) => offlineStore.set(LIST_KEYS.timetableSlots, s)).catch(() => {}),
      timetableService.getSettings().then((s) => offlineStore.set(LIST_KEYS.timetableSettings, s)).catch(() => {}),
    ]);

    const relevant = new Set(topics.map((t) => t.id));
    const [notes, materials] = await Promise.all([
      pb.collection("note_documents").getFullList<NoteDocument>({ requestKey: null }).catch(() => null),
      pb.collection("materials").getFullList<Material>({ requestKey: null }).catch(() => null),
    ]);

    for (const note of notes ?? []) {
      if (!relevant.has(note.topic)) continue;
      const key = noteKey(note.topic, note.kind);
      // Offline-Änderung wartet noch: `base` muss der Stand bleiben, auf dem sie entstand.
      if (await offlineStore.get(`pending:${key}`)) continue;
      await offlineStore.set(`base:${key}`, { noteId: note.id, content: note.content });
    }

    if (materials) {
      const byTopic = new Map<string, Material[]>();
      for (const m of materials) {
        if (relevant.has(m.topic)) byTopic.set(m.topic, [...(byTopic.get(m.topic) ?? []), m]);
      }
      for (const topicId of relevant) {
        await offlineStore.set(materialsKey(topicId), materialsService.sortForDisplay(byTopic.get(topicId) ?? []));
      }
      // Dateien der aktiven Themen aufs Gerät holen, solange gutes Netz da ist
      // (zu Hause) – im Schul-WLAN liegen sie dann schon vor.
      void pullAll(new Map([...relevant].map((t) => [t, byTopic.get(t) ?? []])));
    }
    onProgress?.(topics.length, topics.length);
  },

  /* ---------- Listen (Startseite, Prüfungen, Hausaufgaben) ---------- */

  loadSubjects(): Promise<Subject[] | null> {
    return offlineStore.get<Subject[]>(LIST_KEYS.subjects).then((v) => v ?? null);
  },
  loadTopics(): Promise<Topic[] | null> {
    return offlineStore.get<Topic[]>(LIST_KEYS.topics).then((v) => v ?? null);
  },
  loadHomeworkList(): Promise<Homework[] | null> {
    return offlineStore.get<Homework[]>(LIST_KEYS.homework).then((v) => v ?? null);
  },
  async loadTimetable(): Promise<{ slots: TimetableSlot[]; settings: TimetableSettings } | null> {
    const [slots, settings] = await Promise.all([
      offlineStore.get<TimetableSlot[]>(LIST_KEYS.timetableSlots),
      offlineStore.get<TimetableSettings>(LIST_KEYS.timetableSettings),
    ]);
    return slots && settings ? { slots, settings } : null;
  },
  loadMaterials(topicId: string): Promise<Material[] | null> {
    return offlineStore.get<Material[]>(materialsKey(topicId)).then((v) => v ?? null);
  },
  /**
   * Materialliste ganz ohne Netz: zuletzt bekannte Serverliste plus die
   * Dateien, die bisher nur auf diesem Gerät liegen (noch nicht hochgeladen).
   * `null` = über dieses Thema weiß das Gerät noch gar nichts.
   */
  async materialsLocal(topicId: string): Promise<Material[] | null> {
    const [cached, files] = await Promise.all([
      this.loadMaterials(topicId).catch(() => null),
      localFiles.listTopic(topicId).catch(() => []),
    ]);
    if (!cached && !files.length) return null;
    const list = [...(cached ?? [])];
    const known = new Set(list.map((m) => m.id));
    for (const f of files) if (!known.has(f.id) && f.id.startsWith("local_")) list.push(materialOf(f));
    return materialsService.sortForDisplay(list);
  },
  saveMaterials(topicId: string, materials: Material[]): Promise<void> {
    return offlineStore.set(materialsKey(topicId), materials);
  },
  /** Aus den beiden pro Themen-Notiz gecachten Inhalten (siehe loadNote) eine NoteDocument-Liste rekonstruieren. */
  async loadNotesList(topicId: string): Promise<NoteDocument[]> {
    const out: NoteDocument[] = [];
    for (const kind of ["mitschrift", "lernzettel"] as const) {
      const n = await this.loadNote(topicId, kind);
      if (n) out.push({ id: n.noteId, owner: "", topic: topicId, kind, content: n.content, created: "", updated: "" });
    }
    return out;
  },

  /* ---------- Notizen ---------- */

  async loadNote(topicId: string, kind: NoteKind): Promise<{ noteId: string; content: string } | null> {
    const pending = await offlineStore.get<PendingNote>(`pending:${noteKey(topicId, kind)}`);
    if (pending) return { noteId: pending.noteId, content: pending.content };
    return (await offlineStore.get<{ noteId: string; content: string }>(`base:${noteKey(topicId, kind)}`)) ?? null;
  },

  async loadBaseIfClean(topicId: string, kind: NoteKind): Promise<{ noteId: string; content: string } | null> {
    if (await offlineStore.get(`pending:${noteKey(topicId, kind)}`)) return null;
    return (await offlineStore.get<{ noteId: string; content: string }>(`base:${noteKey(topicId, kind)}`)) ?? null;
  },

  async saveBase(topicId: string, kind: NoteKind, noteId: string, content: string): Promise<void> {
    if (await offlineStore.get(`pending:${noteKey(topicId, kind)}`)) return;
    await offlineStore.set(`base:${noteKey(topicId, kind)}`, { noteId, content });
  },

  async savePendingNote(topicId: string, kind: NoteKind, noteId: string, content: string): Promise<void> {
    await offlineStore.set(`pending:${noteKey(topicId, kind)}`, {
      noteId,
      content,
      savedAt: new Date().toISOString(),
    } satisfies PendingNote);
  },

  /* ---------- Arbeits-PDF ---------- */

  async loadPdf(topicId: string): Promise<OfflinePdfEntry | null> {
    const pending = await offlineStore.get<PendingPdf>(`pending:${pdfKey(topicId)}`);
    const base = await (await loadTopicPdf()).readOfflineTopicPdf(topicId);
    if (!base) return null;
    return pending ? { blob: pending.blob, meta: base.meta, mergeKey: base.mergeKey } : base;
  },

  async savePendingPdf(topicId: string, blob: Blob, rotations: ReadonlyMap<number, number>, mergeKeyAtEdit: string): Promise<void> {
    await offlineStore.set(`pending:${pdfKey(topicId)}`, {
      blob,
      rotations: [...rotations.entries()],
      mergeKeyAtEdit,
      savedAt: new Date().toISOString(),
    } satisfies PendingPdf);
  },

  /** Nach einem normalen Online-Save: eine evtl. hängengebliebene Offline-Version verwirft sich selbst. */
  async clearPendingPdf(topicId: string): Promise<void> {
    await offlineStore.remove(`pending:${pdfKey(topicId)}`);
  },

  /* ---------- Status ---------- */

  async hasPending(): Promise<boolean> {
    const [pending, ops, issues, files] = await Promise.all([
      offlineStore.keysWithPrefix("pending:"),
      offlineStore.keysWithPrefix("opqueue:"),
      loadIssuesRaw(),
      localFiles.pending().catch(() => []),
    ]);
    return pending.length + ops.length + issues.length + files.length > 0;
  },

  /* ---------- Sync-Probleme (Popup am Kreis-Pfeile-Knopf) ---------- */

  loadIssues(): Promise<SyncIssue[]> {
    return loadIssuesRaw();
  },

  /** PDF-Konflikt: Offline-Version verwerfen, Server-Stand gilt (kein automatisches Zusammenführen, siehe syncPdf). */
  async discardPdfConflict(topicId: string): Promise<void> {
    await offlineStore.remove(`pending:${pdfKey(topicId)}`);
    await clearIssueById(`pdf:${topicId}`);
  },

  /** Nur zur Kenntnis nehmen (Notiz wurde bereits automatisch zusammengeführt). */
  dismissIssue(id: string): Promise<void> {
    return clearIssueById(id);
  },

  /** Fehlgeschlagenen Warteschlangen-Eintrag erneut versuchen. */
  async retryOpIssue(issue: SyncIssue): Promise<void> {
    if (!issue.queuedWrite || !issue.queueKey) return;
    await replayWrite(issue.queuedWrite, new Map());
    await offlineStore.remove(issue.queueKey);
    await clearIssueById(issue.id);
  },

  /** Fehlgeschlagenen Warteschlangen-Eintrag verwerfen (Änderung geht verloren – nur wenn der Nutzer das explizit will). */
  async discardOpIssue(issue: SyncIssue): Promise<void> {
    if (issue.queueKey) await offlineStore.remove(issue.queueKey);
    await clearIssueById(issue.id);
  },

  /* ---------- Sync ---------- */

  async sync(): Promise<{ synced: number; conflicts: number; errors: number }> {
    if (!navigator.onLine) throw new Error("Kein Internet.");
    const result = { synced: 0, conflicts: 0, errors: 0 };

    // Erst die einfache Warteschlange (Hausaufgaben/Themen/Fächer – siehe
    // pocketbase-repository.ts). Schlüssel sind zeitstempel-präfixiert, der
    // simple String-Sort ist also auch die Anlagereihenfolge – wichtig, wenn
    // z. B. offline ein Thema angelegt UND direkt eine Hausaufgabe dafür
    // erfasst wurde: die Hausaufgabe referenziert die (lokale) Themen-ID, die
    // erst nach dem Anlegen des Themas durch die echte Server-ID ersetzt
    // werden kann.
    const localIdMap = new Map<string, string>();
    const opKeys = (await offlineStore.keysWithPrefix("opqueue:")).sort();
    for (const key of opKeys) {
      const queued = await offlineStore.get<QueuedWrite>(key);
      if (!queued) {
        await offlineStore.remove(key);
        continue;
      }
      try {
        await replayWrite(queued, localIdMap);
        await offlineStore.remove(key);
        await clearIssueById(`op:${key}`);
        result.synced++;
      } catch (err) {
        result.errors++;
        await saveIssue({
          id: `op:${key}`,
          kind: "op-error",
          message: `${queued.collection} (${queued.op}) konnte nicht synchronisiert werden: ${err instanceof Error ? err.message : "Unbekannter Fehler"}. Die Änderung ist NICHT verloren, sie bleibt in der Warteschlange.`,
          queuedWrite: queued,
          queueKey: key,
          at: new Date().toISOString(),
        });
      }
    }

    // Materialdateien (lokal gespeicherte Anmerkungen, neue Dateien).
    const files = await pushPending();
    result.synced += files.pushed;
    result.errors += files.failed;

    const pendingKeys = await offlineStore.keysWithPrefix("pending:");

    for (const key of pendingKeys) {
      try {
        const noteMatch = /^pending:note:(.+):(mitschrift|lernzettel)$/.exec(key);
        const pdfMatch = /^pending:pdf:(.+)$/.exec(key);
        if (noteMatch) {
          const ok = await syncNote(noteMatch[1], noteMatch[2] as NoteKind);
          ok ? result.synced++ : result.conflicts++;
        } else if (pdfMatch) {
          const ok = await syncPdf(pdfMatch[1]);
          ok ? result.synced++ : result.conflicts++;
        }
      } catch {
        result.errors++;
      }
    }
    return result;
  },
};

/**
 * Spielt einen einzelnen Warteschlangen-Eintrag nach (Hausaufgaben/Themen/
 * Fächer, siehe pocketbase-repository.ts). `idMap` bildet lokale Platzhalter-
 * IDs (`local_xxx`, entstanden beim Offline-Anlegen) auf die echte, vom
 * Server vergebene ID ab – nötig, wenn z. B. offline ein Thema angelegt UND
 * gleich eine Hausaufgabe dafür erfasst wurde: die Hausaufgabe verweist mit
 * `topic`/`subject` auf die lokale ID des Themas, die zu diesem Zeitpunkt
 * schon durch die echte ersetzt sein muss (Reihenfolge kommt aus den
 * zeitstempel-sortierten Schlüsseln in sync()).
 */
async function replayWrite(entry: QueuedWrite, idMap: Map<string, string>): Promise<void> {
  const resolve = (id: string) => idMap.get(id) ?? id;
  const remapRefs = (data: Record<string, unknown> | undefined) => {
    if (!data) return data;
    const out = { ...data };
    for (const [k, v] of Object.entries(out)) {
      if (typeof v === "string" && idMap.has(v)) out[k] = idMap.get(v);
    }
    return out;
  };

  if (entry.op === "create") {
    const { id: _localId, ...rest } = remapRefs(entry.data) ?? {};
    const created = (await pb.collection(entry.collection).create(rest)) as { id: string };
    if (entry.id) idMap.set(entry.id, created.id);
  } else if (entry.op === "update" && entry.id) {
    await pb.collection(entry.collection).update(resolve(entry.id), remapRefs(entry.data));
  } else if (entry.op === "remove" && entry.id) {
    await pb.collection(entry.collection).delete(resolve(entry.id));
  }
  invalidateCollection(entry.collection);
}

/** true = übernommen (kein Konflikt), false = als Konflikt liegen gelassen. */
async function syncNote(topicId: string, kind: NoteKind): Promise<boolean> {
  const pending = await offlineStore.get<PendingNote>(`pending:${noteKey(topicId, kind)}`);
  if (!pending) return true;
  const base = await offlineStore.get<{ content: string }>(`base:${noteKey(topicId, kind)}`);

  const issueId = `note:${topicId}:${kind}`;
  const server = await notesService.getOrCreate(topicId, kind);
  if (server.content === (base?.content ?? "")) {
    // Server unverändert seit dem letzten Online-Stand -> einfach hochladen.
    await notesService.save(pending.noteId, pending.content);
    await clearIssueById(issueId);
  } else {
    // Server hat sich zwischenzeitlich geändert – nichts überschreiben,
    // nur die Offline-Zeilen anhängen, die im neuen Server-Text fehlen. Bleibt
    // trotzdem als Hinweis im Sync-Popup sichtbar, damit das nicht unbemerkt
    // passiert – der Nutzer kann es dort im Thema nachprüfen/manuell glätten.
    const serverLines = new Set(server.content.split("\n"));
    const offlineOnly = pending.content.split("\n").filter((line) => line.trim() && !serverLines.has(line));
    if (offlineOnly.length) {
      const merged = `${server.content.trimEnd()}\n\n### Offline-Notizen\n${offlineOnly.join("\n")}\n`;
      await notesService.save(pending.noteId, merged);
      await saveIssue({
        id: issueId,
        kind: "note-merged",
        topicId,
        noteKind: kind,
        message: `${kind === "mitschrift" ? "Mitschrift" : "Lernzettel"} hatte sich online geändert, während du offline warst – deine Zeilen wurden unten unter „Offline-Notizen" angehängt, nichts wurde überschrieben. Bitte kurz prüfen.`,
        at: new Date().toISOString(),
      });
    } else {
      await clearIssueById(issueId);
    }
  }
  await offlineStore.remove(`pending:${noteKey(topicId, kind)}`);
  await offlineStore.set(`base:${noteKey(topicId, kind)}`, await notesService.getOrCreate(topicId, kind).then((n) => ({ noteId: n.id, content: n.content })));
  return true;
}

async function syncPdf(topicId: string): Promise<boolean> {
  const pending = await offlineStore.get<PendingPdf>(`pending:${pdfKey(topicId)}`);
  if (!pending) return true;

  const { buildTopicPdf, saveTopicPdf, isMedia } = await loadTopicPdf();
  const materials = await materialsService.listByTopic(topicId);
  const sources = materials.filter((m) => !isMedia(m));
  const freshKey = sources.length ? mergeKey(topicId, sources) : "";

  if (freshKey && freshKey === pending.mergeKeyAtEdit) {
    // Materialien unverändert seit der Offline-Bearbeitung -> normaler Save.
    const built = await buildTopicPdf(materials);
    await saveTopicPdf(built, pending.blob, null, new Map(pending.rotations));
    await offlineStore.remove(`pending:${pdfKey(topicId)}`);
    await clearIssueById(`pdf:${topicId}`);
    return true;
  }

  // Materialien haben sich geändert (Datei dazu/weg/anders) – Seiten könnten
  // nicht mehr passen. Lieber liegen lassen als falsch zusammenführen; der
  // Nutzer entscheidet im Sync-Popup (Thema manuell prüfen oder Offline-
  // Version verwerfen).
  await saveIssue({
    id: `pdf:${topicId}`,
    kind: "pdf-conflict",
    topicId,
    message:
      "Die Materialien dieses Themas haben sich online geändert, während du offline im Arbeits-PDF gearbeitet hast. Automatisches Zusammenführen wäre riskant (Seiten könnten nicht mehr passen) – bitte im Thema manuell prüfen.",
    at: new Date().toISOString(),
  });
  return false;
}

